import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';
import ConfirmModal from './ConfirmModal';
import Modal from './Modal';
import Toast from './Toast';
import useFormCRUD from '../hooks/useFormCRUD';

/**
 * Vendor management with full-width table and modal-based Add/Edit forms.
 * Includes address fields, logo display, and vendor settings.
 *
 * @param {Object} props
 * @param {Function} [props.onUpdate] - Callback invoked after create/delete/toggle operations
 * @returns {JSX.Element} The vendor table with Add/Edit modals
 */
function VendorForm({ onUpdate }) {
  const [vendorSearch, setVendorSearch] = useState('');
  const [editingVendor, setEditingVendor] = useState(null);
  const [logoDataUrl, setLogoDataUrl] = useState(null);

  // Fetch logo image with auth when editing a vendor with logo_hash
  useEffect(() => {
    if (!editingVendor?.logo_hash) {
      setLogoDataUrl(null);
      return;
    }
    let cancelled = false;
    const fetchLogo = async () => {
      try {
        const response = await api.getVendorLogo(editingVendor.id);
        if (!cancelled && response.data) {
          setLogoDataUrl(URL.createObjectURL(response.data));
        }
      } catch (err) {
        console.error('Failed to fetch vendor logo:', err);
        if (!cancelled) setLogoDataUrl(null);
      }
    };
    fetchLogo();
    return () => { cancelled = true; };
  }, [editingVendor]);

  const {
    items: vendors,
    formData,
    setFormData,
    editingId,
    showForm,
    setShowForm,
    submitting,
    toast,
    confirmModal,
    handleSubmit,
    handleEdit,
    handleCancelEdit,
    handleDelete,
    handleToggleStatus,
    handleCloseToast,
    handleCloseConfirmModal,
  } = useFormCRUD({
    apiGetAll: () => api.getVendors({ include_inactive: true }),
    apiCreate: api.createVendor,
    apiUpdate: api.updateVendor,
    apiDelete: api.deleteVendor,
    apiToggleStatus: api.toggleVendorStatus,
    initialFormData: {
      name: '',
      contact_info: '',
      primary_contact_name: '',
      primary_contact_phone: '',
      primary_contact_email: '',
      logo_url: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      fuel_surcharge_in_subtotal: false,
      uses_sku: true
    },
    entityName: 'Vendor',
    mapEditData: (v) => ({
      name: v.name || '',
      contact_info: v.contact_info || '',
      primary_contact_name: v.primary_contact_name || '',
      primary_contact_phone: v.primary_contact_phone || '',
      primary_contact_email: v.primary_contact_email || '',
      logo_url: v.logo_url || '',
      address: v.address || '',
      city: v.city || '',
      state: v.state || '',
      zip: v.zip || '',
      fuel_surcharge_in_subtotal: !!v.fuel_surcharge_in_subtotal,
      uses_sku: !!v.uses_sku
    }),
    useConfirmModal: true,
    getItemDisplayName: (item) => item.name,
    onUpdate,
  });

  const filteredVendors = vendorSearch.trim()
    ? vendors.filter(v =>
        v.name.toLowerCase().includes(vendorSearch.toLowerCase()) ||
        (v.primary_contact_name || '').toLowerCase().includes(vendorSearch.toLowerCase())
      )
    : vendors;

  const activeCount = vendors.filter(v => v.status === 'active').length;

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={handleCloseToast} />}
      {confirmModal && (
        <ConfirmModal
          isOpen={true}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          variant={confirmModal.variant}
          onConfirm={confirmModal.onConfirm}
          onCancel={handleCloseConfirmModal}
        />
      )}

      {/* Edit Vendor Modal */}
      <Modal
        isOpen={!!editingId}
        onClose={() => { handleCancelEdit(); setShowForm(false); setEditingVendor(null); }}
        title="Edit Vendor"
        size="large"
      >
        {/* Logo preview when editing */}
        {editingId && (logoDataUrl || formData.logo_url) && (
          <div className="vendor-form__logo-preview">
            <img
              src={logoDataUrl || formData.logo_url}
              alt={`${formData.name} logo`}
              className="vendor-form__logo-img"
            />
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ color: 'var(--error)', marginTop: 'var(--sp2)' }}
              onClick={async () => {
                setFormData({ ...formData, logo_url: '' });
                if (editingVendor?.logo_hash) {
                  try {
                    await api.deleteVendorLogo(editingId);
                  } catch { /* ignore */ }
                  setEditingVendor({ ...editingVendor, logo_hash: null });
                  setLogoDataUrl(null);
                }
              }}
            >
              Remove logo
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="field">
            <label className="label" htmlFor="edit-vendor-name">Vendor Name <span className="req">*</span></label>
            <input
              id="edit-vendor-name"
              className="input"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              aria-required="true"
            />
          </div>

          {/* Contact Info */}
          <div className="field">
            <label className="label" htmlFor="edit-vendor-contact-name">Contact Name</label>
            <input
              id="edit-vendor-contact-name"
              className="input"
              type="text"
              value={formData.primary_contact_name}
              onChange={(e) => setFormData({...formData, primary_contact_name: e.target.value})}
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="edit-vendor-contact-phone">Phone</label>
            <input
              id="edit-vendor-contact-phone"
              className="input"
              type="tel"
              value={formData.primary_contact_phone}
              onChange={(e) => setFormData({...formData, primary_contact_phone: e.target.value})}
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="edit-vendor-contact-email">Email</label>
            <input
              id="edit-vendor-contact-email"
              className="input"
              type="email"
              value={formData.primary_contact_email}
              onChange={(e) => setFormData({...formData, primary_contact_email: e.target.value})}
            />
          </div>

          {/* Address */}
          <div className="field">
            <label className="label" htmlFor="edit-vendor-address">Address</label>
            <input
              id="edit-vendor-address"
              className="input"
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              placeholder="Street address"
            />
          </div>
          <div className="vendor-form__address-row">
            <div className="field">
              <label className="label" htmlFor="edit-vendor-city">City</label>
              <input
                id="edit-vendor-city"
                className="input"
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
              />
            </div>
            <div className="field">
              <label className="label" htmlFor="edit-vendor-state">State</label>
              <input
                id="edit-vendor-state"
                className="input"
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({...formData, state: e.target.value})}
                maxLength={2}
              />
            </div>
            <div className="field">
              <label className="label" htmlFor="edit-vendor-zip">Zip</label>
              <input
                id="edit-vendor-zip"
                className="input"
                type="text"
                value={formData.zip}
                onChange={(e) => setFormData({...formData, zip: e.target.value})}
                maxLength={10}
              />
            </div>
          </div>

          {/* Settings */}
          <div className="settings-form__settings-group">
            <label className="settings-form__checkbox">
              <input
                type="checkbox"
                checked={formData.fuel_surcharge_in_subtotal}
                onChange={(e) => setFormData({...formData, fuel_surcharge_in_subtotal: e.target.checked})}
              />
              Fuel surcharge in subtotal
            </label>
            <label className="settings-form__checkbox">
              <input
                type="checkbox"
                checked={formData.uses_sku}
                onChange={(e) => setFormData({...formData, uses_sku: e.target.checked})}
              />
              Uses SKU numbers
            </label>
          </div>

          <div className="settings-form__actions">
            <button type="submit" className="btn btn-primary btn-sm" disabled={submitting} style={{ flex: 1 }}>
              {submitting ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => { handleCancelEdit(); setShowForm(false); setEditingVendor(null); }}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {/* Add Vendor Modal */}
      <Modal
        isOpen={showForm && !editingId}
        onClose={() => setShowForm(false)}
        title="Add Vendor"
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="field">
            <label className="label" htmlFor="vendor-name">Vendor Name <span className="req">*</span></label>
            <input
              id="vendor-name"
              className="input"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              aria-required="true"
            />
          </div>

          {/* Contact Info */}
          <div className="field">
            <label className="label" htmlFor="vendor-contact-name">Contact Name</label>
            <input
              id="vendor-contact-name"
              className="input"
              type="text"
              value={formData.primary_contact_name}
              onChange={(e) => setFormData({...formData, primary_contact_name: e.target.value})}
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="vendor-contact-phone">Phone</label>
            <input
              id="vendor-contact-phone"
              className="input"
              type="tel"
              value={formData.primary_contact_phone}
              onChange={(e) => setFormData({...formData, primary_contact_phone: e.target.value})}
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="vendor-contact-email">Email</label>
            <input
              id="vendor-contact-email"
              className="input"
              type="email"
              value={formData.primary_contact_email}
              onChange={(e) => setFormData({...formData, primary_contact_email: e.target.value})}
            />
          </div>

          {/* Address */}
          <div className="field">
            <label className="label" htmlFor="vendor-address">Address</label>
            <input
              id="vendor-address"
              className="input"
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              placeholder="Street address"
            />
          </div>
          <div className="vendor-form__address-row">
            <div className="field">
              <label className="label" htmlFor="vendor-city">City</label>
              <input
                id="vendor-city"
                className="input"
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
              />
            </div>
            <div className="field">
              <label className="label" htmlFor="vendor-state">State</label>
              <input
                id="vendor-state"
                className="input"
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({...formData, state: e.target.value})}
                maxLength={2}
              />
            </div>
            <div className="field">
              <label className="label" htmlFor="vendor-zip">Zip</label>
              <input
                id="vendor-zip"
                className="input"
                type="text"
                value={formData.zip}
                onChange={(e) => setFormData({...formData, zip: e.target.value})}
                maxLength={10}
              />
            </div>
          </div>

          {/* Settings */}
          <div className="settings-form__settings-group">
            <label className="settings-form__checkbox">
              <input
                type="checkbox"
                checked={formData.fuel_surcharge_in_subtotal}
                onChange={(e) => setFormData({...formData, fuel_surcharge_in_subtotal: e.target.checked})}
              />
              Fuel surcharge in subtotal
            </label>
            <label className="settings-form__checkbox">
              <input
                type="checkbox"
                checked={formData.uses_sku}
                onChange={(e) => setFormData({...formData, uses_sku: e.target.checked})}
              />
              Uses SKU numbers
            </label>
          </div>

          <div className="settings-form__actions">
            <button type="submit" className="btn btn-primary btn-sm" disabled={submitting} style={{ flex: 1 }}>
              {submitting ? 'Saving...' : 'Add Vendor'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      <div className="card">
        <div className="settings-form__table-header">
          <h3 className="settings-form__section-title" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
            Vendors ({activeCount} active{vendors.length !== activeCount ? `, ${vendors.length - activeCount} inactive` : ''})
          </h3>
          <div style={{ display: 'flex', gap: 'var(--sp2)', alignItems: 'center' }}>
            {vendors.length > 3 && (
              <input
                className="input settings-form__search-input"
                type="text"
                placeholder="Search..."
                value={vendorSearch}
                onChange={(e) => setVendorSearch(e.target.value)}
                aria-label="Search vendors"
              />
            )}
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>+ Add Vendor</button>
          </div>
        </div>

        {vendors.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__title">No vendors yet</div>
            <div className="empty-state__description">Add your first vendor to get started.</div>
          </div>
        ) : (
          <div className="tbl-wrap tbl-wrap--md">
            <table>
              <thead>
                <tr>
                  <th scope="col">Vendor</th>
                  <th scope="col">Contact</th>
                  <th scope="col">Location</th>
                  <th scope="col">Settings</th>
                  <th scope="col">Status</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {filteredVendors.map(v => (
                  <tr
                    key={v.id}
                    className={`${v.status === 'inactive' ? 'row-inactive' : ''} ${editingId === v.id ? 'table-row--editing' : ''}`.trim() || undefined}
                  >
                    <td>
                      <div className="vendor-form__vendor-cell">
                        {v.logo_url ? (
                          <img src={v.logo_url} alt="" className="vendor-form__table-logo" aria-hidden="true" onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : (
                          <span className="vendor-form__logo-placeholder" aria-hidden="true">
                            {v.name.charAt(0).toUpperCase()}
                          </span>
                        )}
                        <span className="vendor-form__vendor-name">{v.name}</span>
                      </div>
                    </td>
                    <td>
                      {v.primary_contact_name && <div>{v.primary_contact_name}</div>}
                      {v.primary_contact_phone && <div className="settings-form__meta">{v.primary_contact_phone}</div>}
                      {v.primary_contact_email && <div className="settings-form__meta">{v.primary_contact_email}</div>}
                      {!v.primary_contact_name && !v.primary_contact_phone && !v.primary_contact_email && <span className="settings-form__meta">—</span>}
                    </td>
                    <td>
                      {(v.city || v.state) ? (
                        <span>{[v.city, v.state].filter(Boolean).join(', ')}</span>
                      ) : (
                        <span className="settings-form__meta">—</span>
                      )}
                    </td>
                    <td>
                      <div className="settings-form__settings-group-badges">
                        {v.fuel_surcharge_in_subtotal ? <span className="badge">Fuel in subtotal</span> : null}
                        {v.uses_sku ? <span className="badge">SKU</span> : null}
                        {!v.fuel_surcharge_in_subtotal && !v.uses_sku && <span className="settings-form__meta">—</span>}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge status-${v.status}`}>
                        {v.status === 'active' ? '\u2713' : '\u2717'} {v.status}
                      </span>
                    </td>
                    <td>
                      <div className="settings-form__action-btns">
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => { setEditingVendor(v); handleEdit(v); }}
                          aria-label={'Edit ' + v.name}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleToggleStatus(v.id)}
                          aria-label={(v.status === 'active' ? 'Disable ' : 'Enable ') + v.name}
                          style={{ color: v.status === 'active' ? 'var(--warning)' : 'var(--success)' }}
                        >
                          {v.status === 'active' ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleDelete(v.id, v.name)}
                          aria-label={'Delete ' + v.name}
                          style={{ color: 'var(--error)' }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

VendorForm.propTypes = {
  /** Callback invoked after create/delete/toggle operations */
  onUpdate: PropTypes.func,
};

export default VendorForm;
